// GenieX Dynamic Series — content and imagery.
// Inspired by Apple's product philosophy, reimagined for the Indian home.
// Authoring style: declarative, restrained, considered.

// Image catalogue from the Island Series CDN — verified by visual inspection.
// device14 → ~4-key compact   · device12 → ~6-key classic
// device13 → ~10-key wide     · device15 → ~12-key multi-panel
// device16 → 6-key in lifestyle/in-wall context (used as hero close-up)
const IMG = {
  compact: "https://www.islandseries.in/assets/device14-g1ObQUzk.jpg",
  classic: "https://www.islandseries.in/assets/device12-CDqRihe1.jpg",
  wide: "https://www.islandseries.in/assets/device13-DTqQeWN0.jpg",
  multi: "https://www.islandseries.in/assets/device15-CCbOn_pg.jpg",
  lifestyle: "https://www.islandseries.in/assets/device16-DPjrjGtv.jpg",
};

// Hero loop — labelled by MODULAR SIZE (the standard Indian frame width),
// not button count. Image of each slide actually matches its module size.
export const DS_HERO_DEVICES = [
  { src: IMG.compact, label: "4M · Compact", note: "Slim. Corridors. Bedrooms." },
  { src: IMG.classic, label: "6M · Classic", note: "The everyday hero." },
  { src: IMG.wide, label: "8M · Wide", note: "Theatres. Studies. Suites." },
  { src: IMG.multi, label: "12M · Multi", note: "The orchestrator. Foyers. Masters." },
];

// Module form-factor lineup — four modular sizes (Indian standard).
export const DS_MODULES = [
  {
    name: "Dynamic 4M",
    blurb: "Slim and quiet. For corridors, bathrooms and reading nooks.",
    img: IMG.compact,
    spec: "4-module · upto 4 scenes · always-on display",
  },
  {
    name: "Dynamic 6M",
    blurb: "The everyday hero. For bedrooms, dining and living.",
    img: IMG.classic,
    spec: "6-module · 6 scenes · ambient display",
  },
  {
    name: "Dynamic 8M",
    blurb: "The command surface. For theatres, studies and suites.",
    img: IMG.wide,
    spec: "8-module · 10 scenes · interactive display",
  },
  {
    name: "Dynamic 12M Multi",
    blurb: "The orchestrator. For foyers and master suites.",
    img: IMG.multi,
    spec: "12-module · 12 scenes · live status legend",
  },
];

// Single hero close-up image (used in the Sculpted philosophy section).
export const DS_HERO_CLOSEUP = IMG.lifestyle;

// Seven curated finishes — each with a real colour swatch asset.
// (Graphite was removed: it shared an image with the product photo and so
// looked broken next to the proper swatch PNGs.)
export const DS_FINISHES = [
  { name: "Matte Black", hex: "#0E0E11", img: "https://www.islandseries.in/assets/black-DONSpqz4.png" },
  { name: "Pearl White", hex: "#EFE9DE", img: "https://www.islandseries.in/assets/white-_F2Ke-Xz.png" },
  { name: "Champagne Gold", hex: "#C9A961", img: "https://www.islandseries.in/assets/gold-ZLu2jpz7.jpg" },
  { name: "Pearl Silver", hex: "#C7C7CD", img: "https://www.islandseries.in/assets/silver-BwB7Xjvi.png" },
  { name: "Terracotta", hex: "#B85C38", img: "https://www.islandseries.in/assets/orange-CqaZarGY.png" },
  { name: "Sage Green", hex: "#7A8F6B", img: "https://www.islandseries.in/assets/green-B-vR_EK9.png" },
  { name: "Navy Blue", hex: "#1F2E48", img: "https://www.islandseries.in/assets/blue-BRmgBfTy.png" },
];

// The four pillar features (Always-on, Tactile, Engineered, Mesh).
export const DS_PILLARS = [
  {
    icon: "Sparkles",
    title: "Always-on display",
    body:
      "Time, weather and ambient information — rendered on a curved oleophobic glass that stays dark until you need it.",
  },
  {
    icon: "Hand",
    title: "Tactile premium keys",
    body:
      "Hand-finished, micro-textured keys with a confident, deliberate click. Engineered for ten million presses.",
  },
  {
    icon: "Cpu",
    title: "Modular backplate",
    body:
      "Fits any standard Indian modular box — 4M, 6M, 8M or 12M. No new wiring. No drama.",
  },
  {
    icon: "Wifi",
    title: "Zigbee 3.0 mesh",
    body:
      "A self-healing local mesh — your home keeps working, even when the internet doesn't.",
  },
];

// "Inspired by Apple" philosophy block — short, declarative statements that
// celebrate the design lineage we proudly stand on.
export const DS_PHILOSOPHY = {
  eyebrow: "Inspired by Apple. Crafted in India.",
  title: "Designed in the footsteps of\u00a0Steve\u00a0Jobs.",
  quote:
    "Design is not just what it looks like and feels like. Design is how it works.",
  quoteBy: "Steve Jobs",
  body:
    "Every chamfer, every grain of glass, every micron of travel under your fingertip — considered, then reconsidered, until nothing is left to remove. We don't build a switch with extra features. We build a single, sculpted surface that quietly puts your entire home under your fingers.",
  beats: [
    {
      title: "Simplicity is the ultimate sophistication",
      body:
        "One surface. One language. No app required to live with it day-to-day. Everything happens on the wall.",
    },
    {
      title: "Details are not details. They make the design.",
      body:
        "0.2 mm chamfers. Hand-deburred edges. Backlit icons that breathe with the room. Every label, every screw — intentional.",
    },
    {
      title: "Real artists ship.",
      body:
        "Tuned for Indian voltage, Indian boxes and Indian summers. Same-week service across South India. Five-year warranty in writing.",
    },
  ],
};
